import { ArrowRight, CircleCheck, Clock3, LoaderCircle, TriangleAlert } from "lucide-react";
import { Link } from "react-router";

export function SignupAccountStatus({
  status,
  email,
  created,
  onRetry,
  onClose,
}: {
  status: "loading" | "error" | "received" | "approved";
  email?: string | null;
  created?: boolean;
  onRetry?: () => void;
  onClose?: () => void;
}) {
  const Heading = onClose ? "h2" : "h1";
  const Icon =
    status === "loading" ? LoaderCircle : status === "error" ? TriangleAlert : CircleCheck;
  const badge =
    status === "approved"
      ? "Ready to build"
      : status === "received"
        ? "Pending approval"
        : status === "error"
          ? "Status unavailable"
          : "Updating status";

  return (
    <section
      className={`panel signup-status-card signup-status-card--${status}`}
      aria-labelledby="signup-account-status-title"
    >
      <div className="signup-status-top">
        <div className="signup-status-mark" aria-hidden="true">
          <Icon size={30} strokeWidth={1.5} />
        </div>
        <span className="signup-status-badge">{badge}</span>
      </div>

      <div className="signup-status-copy">
        <p className="signup-status-eyebrow">Woven early access</p>
        <Heading id="signup-account-status-title" className="signup-status-title">
          {status === "approved"
            ? "Access approved"
            : status === "received"
              ? "Request received"
              : status === "error"
                ? "Unable to check your account"
                : "Checking your account…"}
        </Heading>
        <p className="signup-status-message" role={status === "error" ? "alert" : "status"}>
          {status === "error"
            ? "Your saved account status couldn't be verified. Please try again."
            : status === "loading"
              ? "Checking your saved request and latest access status…"
              : status === "approved"
                ? "Your account has been approved. You can now open the console."
                : created === false
                  ? "Your request has already been received, but we appreciate the enthusiasm."
                  : "Your request has been received, and we will reach out when availability opens."}
        </p>
      </div>

      {status === "received" && (
        <div className="signup-status-note">
          <Clock3 size={20} strokeWidth={1.5} aria-hidden="true" />
          <div>
            <p className="signup-status-note-title">You're on the list</p>
            <p>
              Your request is saved to your account. No need to submit it again—return here any time
              to check your access status.
            </p>
            {email && (
              <p className="signup-status-email">
                Watch <strong>{email}</strong> for an email when availability opens.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="signup-status-actions">
        {status === "error" &&
          (onRetry ? (
            <button type="button" className="btn btn--primary" onClick={onRetry}>
              Retry account status
            </button>
          ) : (
            <Link to="/sign-in" className="btn btn--primary" onClick={onClose}>
              Sign In
            </Link>
          ))}
        {status === "approved" && (
          <Link to="/console" className="btn btn--primary" onClick={onClose}>
            Open Console
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        )}
        <Link
          to="/"
          className={`btn ${status === "received" ? "btn--primary" : "btn--ghost"}`}
          onClick={onClose}
        >
          Back to home
        </Link>
        {status === "received" && (
          <Link to="/docs" className="btn btn--ghost" onClick={onClose}>
            Explore the docs
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        )}
      </div>
    </section>
  );
}
